import classNames from "classnames";

import { Privilege } from "services/rbac/types";

import DatabaseIcon from "assets/icons/workspace/Header/DatabaseHeader.svg?react";
import EngineIcon from "assets/icons/workspace/Header/EngineHeader.svg?react";
import SecurityIcon from "assets/icons/workspace/Header/SecurityHeader.svg?react";

import { rolesPrivileges, usersPrivileges } from "pages/govern/privileges";

import { useLayoutToggles } from "components/LayoutToggles/context";
import Tooltip from "components/Tooltip";

import { useCurrentAccount } from "../Account/useCurrentAccount";
import { useAccessManager } from "../App/accessManager";
import { CreateMenu } from "./CreateMenu/CreateMenu";
import { DatabasesRefresher } from "./DatabasesRefresher";
import { HeaderIcon } from "./HeaderIcon/HeaderIcon";
import { LeftSidebarTabs } from "./types";

import styles from "./styles.module.scss";

const tabs = [
  {
    type: LeftSidebarTabs.DATABASES,
    title: "Databases",
    icon: <DatabaseIcon />,
    testId: "explore-databases",
    isAvailable: () => true,
  },
  {
    type: LeftSidebarTabs.ENGINES,
    title: "Engines",
    icon: <EngineIcon />,
    testId: "explore-engines",
    isAvailable: () => !process.env.REACT_APP_LITE,
  },
  {
    type: LeftSidebarTabs.SECURITY,
    title: "Security",
    icon: <SecurityIcon />,
    testId: "explore-security",
    isAvailable: (
      isAllowedTo: ReturnType<typeof useAccessManager>["isAllowedTo"],
      accountName: string
    ) => {
      const privilegeMatch = (privilege: Privilege) =>
        isAllowedTo(
          privilege.objectType,
          privilege.objectName,
          privilege.privilegeType
        );

      return (
        // as for Govern -> Users
        usersPrivileges(accountName).some(privilegeMatch) ||
        // as for Govern -> Roles
        rolesPrivileges(accountName).some(privilegeMatch)
      );
    },
  },
];

type Props = {
  activeTab: LeftSidebarTabs;
  setTab: (tab: LeftSidebarTabs) => void;
};

export const Header = (props: Props) => {
  const { setTab, activeTab } = props;
  const { layout, setLayout } = useLayoutToggles();
  const { isAllowedTo } = useAccessManager();
  const { getAccount } = useCurrentAccount();
  const isLite = Boolean(process.env.REACT_APP_LITE);

  const accountName = getAccount().accountName;

  return (
    <div
      className={styles.header}
      data-testid="object-exporer-header"
    >
      {tabs
        .filter(tab => {
          const { isAvailable } = tab;
          return isAvailable(isAllowedTo, accountName);
        })
        .map(tab => {
          const { type, icon, title, testId } = tab;
          const selected = activeTab === type;
          return (
            <button
              key={type}
              onClick={() => {
                if (type === activeTab) {
                  return;
                }
                setTab(type);
                setLayout({ ...layout, engines: { expanded: false } });
              }}
              className={classNames(styles.tab, {
                [styles.active]: selected,
              })}
              data-testid={testId}
            >
              <Tooltip
                title={selected ? null : title}
                placement="bottom"
              >
                <HeaderIcon
                  icon={icon}
                  selected={selected}
                />
              </Tooltip>
              <span className={styles.title}>{title}</span>
            </button>
          );
        })}
      {!isLite && <CreateMenu />}
      {isLite && <DatabasesRefresher />}
    </div>
  );
};
